// SPDX-License-Identifier: AGPL-3.0-or-later
import { ExtensionUrl, findExtension, } from '@intakeform/fhir';
import { indexResponseItems, walkQuestionnaireItems } from './answers.js';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
/**
 * Freshness pass: PLAN.md §5 step 5. For each active answer with an
 * `answered-at` extension, compute `(now - answeredAt)` and compare to the
 * effective freshness threshold:
 *
 *   item-level `freshness-days` overrides form-level, which overrides the
 *   org default supplied by the orchestrator.
 *
 * Stale answers emit a `warning` finding with code `stale`. Per PLAN.md
 * §8 (decision 2), warnings flag the response just like errors do.
 */
export function runFreshnessPass(questionnaire, response, activeLinkIds, now, defaultFreshnessDays) {
    const findings = [];
    const responseItems = indexResponseItems(response);
    const formDefault = extractDays(findExtension(questionnaire, ExtensionUrl.freshnessDays));
    for (const item of walkQuestionnaireItems(questionnaire.item)) {
        if (!activeLinkIds.has(item.linkId))
            continue;
        const itemThreshold = extractDays(findExtension(item, ExtensionUrl.freshnessDays));
        const threshold = itemThreshold ?? formDefault ?? defaultFreshnessDays;
        if (threshold === undefined)
            continue;
        const r = responseItems.get(item.linkId);
        if (r === undefined)
            continue;
        for (const a of r.answer ?? []) {
            const answeredAt = readAnsweredAt(a);
            if (answeredAt === undefined)
                continue;
            const ageDays = (now.getTime() - answeredAt.getTime()) / MS_PER_DAY;
            if (ageDays > threshold) {
                findings.push({
                    linkId: item.linkId,
                    severity: 'warning',
                    code: 'stale',
                    message: `Answer is older than the ${threshold}-day freshness threshold.`,
                    context: { ageDays: Math.floor(ageDays), threshold },
                });
            }
        }
    }
    return findings;
}
function extractDays(ext) {
    if (ext === undefined)
        return undefined;
    if (ext.valueInteger !== undefined)
        return ext.valueInteger;
    if (ext.valueDecimal !== undefined)
        return ext.valueDecimal;
    return undefined;
}
function readAnsweredAt(answer) {
    const ext = (answer.extension ?? []).find((e) => e.url === ExtensionUrl.answeredAt);
    if (ext === undefined)
        return undefined;
    const ts = ext.valueDateTime ?? ext.valueDate;
    if (ts === undefined)
        return undefined;
    const parsed = new Date(ts);
    if (Number.isNaN(parsed.getTime()))
        return undefined;
    return parsed;
}
//# sourceMappingURL=freshness.js.map